import { useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import './DashboardAIChat.css';
import { API_URL } from '../../config/api';

const STARTERS = {
  pipeline: [
    'Current pipeline?',
    'Top owners',
    'Pipeline by region',
    'Which deals need attention?',
    'Coverage vs target',
    'Stage concentration',
  ],
  arr: [
    'Current ARR?',
    'Top customers',
    'ARR by region',
    'GRR analysis',
    'NRR breakdown',
    'Who churned?',
    'Top sales reps',
    'Product mix',
    'ARR trend',
    'Customer risk',
  ],
  ar: [
    'Current AR?',
    'Top overdue customers',
    'AR by region',
    '91+ bucket risk',
    'Pending renewals',
    'Collection focus',
  ],
  legal: [
    'Documents by status?',
    'Oldest open documents',
    'Overdue documents',
    'Top requestors',
    'By document category',
    'By country',
  ],
};

const WELCOME_TEXT =
  "Ask me about KPIs, customers, reps, regions, churn, risk, trends, or deal focus, and I'll answer using your current dashboard data.";

function cleanMessages(messages) {
  return messages
    .filter((message) => message.role === 'user' || message.role === 'assistant')
    .slice(-8)
    .map(({ role, content }) => ({ role, content }));
}

function CloseIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M6 6l12 12M18 6 6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function WidthIcon({ expanded }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      {expanded ? (
        <>
          <path d="M9 7 4 12l5 5M15 7l5 5-5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M4 12h5M20 12h-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </>
      ) : (
        <>
          <path d="m8 7-5 5 5 5M16 7l5 5-5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M3 12h5M21 12h-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </>
      )}
    </svg>
  );
}

function SendIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M4.5 19.5 20 12 4.5 4.5l2.3 6.6a1 1 0 0 0 .78.67L14 12l-6.42.23a1 1 0 0 0-.78.67L4.5 19.5Z"
        stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function SparkleIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 3.5 13.9 8.6 19 10.5l-5.1 1.9L12 17.5l-1.9-5.1L5 10.5l5.1-1.9L12 3.5Z" fill="currentColor" />
      <path d="M18.5 14.5 19.2 16.3 21 17l-1.8.7-.7 1.8-.7-1.8-1.8-.7 1.8-.7.7-1.8Z" fill="currentColor" />
    </svg>
  );
}

function UserGlyph() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="8.2" r="3.4" stroke="currentColor" strokeWidth="1.7" />
      <path d="M4.8 19.5c1.35-3.4 4.2-5.2 7.2-5.2s5.85 1.8 7.2 5.2" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  );
}

export function DashboardAIChat({ dashboard, activeTab, filters = {} }) {
  const [open, setOpen] = useState(false);
  const [widthExpanded, setWidthExpanded] = useState(false);
  const [question, setQuestion] = useState('');
  const [busy, setBusy] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'assistant', content: `Hi, I'm your 1Kosmos dashboard assistant. ${WELCOME_TEXT}` },
  ]);
  const scrollAnchorRef = useRef(null);
  const inputRef = useRef(null);

  const visibleFilters = useMemo(
    () => Object.fromEntries(Object.entries(filters).filter(([, value]) => value)),
    [filters],
  );

  useEffect(() => {
    if (!open) return;
    scrollAnchorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages, busy, open]);

  useEffect(() => {
    if (!open) return;
    const id = setTimeout(() => inputRef.current?.focus(), 180);
    return () => clearTimeout(id);
  }, [open]);

  const ask = async (text) => {
    const prompt = text.trim();
    if (!prompt || busy) return;

    const nextMessages = [...messages, { role: 'user', content: prompt }];
    setMessages(nextMessages);
    setQuestion('');
    setBusy(true);

    try {
      const response = await fetch(`${API_URL}/api/ai/chat/`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          dashboard,
          active_tab: activeTab,
          filters: visibleFilters,
          question: prompt,
          messages: cleanMessages(messages),
        }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'AI assistant is unavailable.');
      setMessages([...nextMessages, { role: 'assistant', content: result.answer }]);
    } catch (error) {
      setMessages([
        ...nextMessages,
        { role: 'assistant', content: error.message },
      ]);
    } finally {
      setBusy(false);
    }
  };

  const contextLabel = activeTab ? `${dashboard.toUpperCase()} · ${activeTab}` : dashboard.toUpperCase();
  const isEmpty = messages.length === 1;
  const starters = STARTERS[dashboard] || STARTERS.pipeline;

  return (
    <div className={`ai-chat${open ? ' open' : ''}`}>
      {open && <button className="ai-chat-backdrop" type="button" aria-label="Close AI chat" onClick={() => setOpen(false)} />}

      {open && (
        <div className={`ai-chat-panel${widthExpanded ? ' ai-chat-panel-wide' : ''}`} role="dialog" aria-label="Dashboard AI assistant">
          <div className="ai-chat-head">
            <div className="ai-chat-head-icon"><SparkleIcon /></div>
            <div className="ai-chat-head-text">
              <div className="ai-chat-title">1Kosmos AI Assistant</div>
              <div className="ai-chat-subtitle">
                <span className="ai-status" aria-hidden="true" />
                {contextLabel}
              </div>
            </div>
            <button
              className="ai-icon-btn ai-width-btn"
              type="button"
              onClick={() => setWidthExpanded((value) => !value)}
              aria-label={widthExpanded ? 'Restore AI chat width' : 'Expand AI chat width'}
              aria-pressed={widthExpanded}
              title={widthExpanded ? 'Restore width' : 'Expand width'}
            >
              <WidthIcon expanded={widthExpanded} />
            </button>
            <button className="ai-icon-btn" type="button" onClick={() => setOpen(false)} aria-label="Close AI chat">
              <CloseIcon />
            </button>
          </div>

          <div className="ai-chat-messages">
            {isEmpty ? (
              <div className="ai-empty-state">
                <div className="ai-empty-icon"><SparkleIcon /></div>
                <div className="ai-empty-title">Hi, I&rsquo;m your 1Kosmos Assistant</div>
                <p className="ai-empty-text">{WELCOME_TEXT}</p>
              </div>
            ) : (
              messages.map((message, index) => (
                <div key={`${message.role}-${index}`} className={`ai-row ${message.role}`}>
                  {message.role === 'assistant' && (
                    <div className="ai-avatar ai-avatar-bot"><SparkleIcon /></div>
                  )}
                  <div className={`ai-message ${message.role}`}>
                    {message.role === 'assistant' ? (
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.content}</ReactMarkdown>
                    ) : (
                      message.content
                    )}
                  </div>
                  {message.role === 'user' && (
                    <div className="ai-avatar ai-avatar-user"><UserGlyph /></div>
                  )}
                </div>
              ))
            )}

            {busy && (
              <div className="ai-row assistant">
                <div className="ai-avatar ai-avatar-bot"><SparkleIcon /></div>
                <div className="ai-message assistant ai-typing" aria-label="Assistant is thinking">
                  <span /><span /><span />
                </div>
              </div>
            )}
            <div ref={scrollAnchorRef} />
          </div>

          <div className="ai-starters">
            <div className="ai-starters-label">Try asking</div>
            <div className="ai-starters-row">
              {starters.map((starter) => (
                <button key={starter} type="button" onClick={() => ask(starter)} disabled={busy}>
                  {starter}
                </button>
              ))}
            </div>
          </div>

          <form
            className="ai-chat-form"
            onSubmit={(event) => {
              event.preventDefault();
              ask(question);
            }}
          >
            <input
              ref={inputRef}
              value={question}
              onChange={(event) => setQuestion(event.target.value)}
              placeholder="Ask about this dashboard..."
              disabled={busy}
            />
            <button type="submit" className="ai-send-btn" disabled={busy || !question.trim()} aria-label="Send question">
              <SendIcon />
            </button>
          </form>
        </div>
      )}

      {!open && (
        <button
          className="ai-chat-launcher"
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Open AI assistant"
          aria-expanded={false}
        >
          AI
        </button>
      )}
    </div>
  );
}
